/*!
  Pioneer WFRIA MegaMenu v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MegaMenu"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MegaMenu") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MegaMenu");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["childrenAriaLabel", "header", "children"],
      _excluded2 = ["target", "url", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { cloneElement, useContext, useEffect, useRef, useState } from "react";
import { KEYBOARD_KEYS, LINK, MEGAMENU, MEGAMENUITEMGROUP, NAVLINK } from "../common/constants";
import { invalidIf, requiredIf } from "../common/proptypes";
import { NavBarContext } from "../NavBar/NavBarContext";
import { OVERRIDE } from "../themes/common/classes";
import extractTheme from "./extractTheme";
import { Anchor, IconForward } from "..";
/**
 * MegaMenu is a React component that renders a menu with list of clickable
 * anchor links. MegaMenu can only be used as a child of NavBarItem component.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function MegaMenu(_ref) {
  let {
    childrenAriaLabel,
    header,
    children
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    navItemsDropDown,
    handleKeyboardActions,
    setShowMegaMenu,
    lastNavBarItem
  } = useContext(NavBarContext); // Holds index of active element in MegaMenu.

  const [activeElement, setActiveElement] = useState(0);
  const megaMenuRef = useRef(null); // Holds the active elements of MegaMenu.

  const focusableElements = useRef(null);
  const {
    megaMenuList,
    headerMegaMenu,
    icon,
    wrapper,
    hopLinkWrapper
  } = extractTheme(_objectSpread({
    theme,
    navItemsDropDown
  }, otherProps));
  /**
   * This method validates the value of `routeTo` in `header` prop and returns a
   * renderable component.
   * @returns An V4 Anchor component or user passed component(NavLink, Link).
   */

  const renderHubLink = () => {
    var _routeTo$type;

    const {
      routeTo,
      disabled,
      label
    } = header;
    const routeToType = typeof routeTo;
    let itemProps = {
      buttonStyles: headerMegaMenu,
      disabled,
      rightIcon: _jsx(IconForward, {
        theme: "inverse",
        size: "xsmall",
        decorative: true
      })
    };
    const isObjectType = routeToType === "object" && !((_routeTo$type = routeTo.type) !== null && _routeTo$type !== void 0 && _routeTo$type.displayName);

    if (isObjectType) {
      const {
        target = "_self",
        url,
        onClick
      } = routeTo,
            otherRouteToProps = _objectWithoutProperties(routeTo, _excluded2);

      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        target,
        href: url,
        tabIndex: disabled ? -1 : 0,
        onClick: onClick && (e => onClick(e, _objectSpread({
          target,
          url
        }, otherRouteToProps)))
      }, otherRouteToProps);
    } else if (routeToType === "string") {
      itemProps = _objectSpread(_objectSpread({}, itemProps), {}, {
        href: routeTo
      });
    }

    if (routeToType === "string" || isObjectType) {
      return _jsx(Anchor, _objectSpread(_objectSpread({}, itemProps), {}, {
        children: label
      }));
    } else {
      return _jsx("div", {
        css: wrapper,
        children: /*#__PURE__*/cloneElement(routeTo, {
          className: "pnr-base-button pnr-megamenu"
        }, [routeTo.props.children, _jsx(IconForward, {
          style: icon,
          size: "xsmall",
          decorative: true
        }, "hopLinkIcon")])
      });
    }
  };
  /**
   * This event handler executes during `keyDown` action in MegaMenu.
   * @param {event} event - Event object.
   */
  // eslint-disable-next-line complexity


  const handleKeydown = event => {
    const {
      TAB,
      UP,
      LEFT,
      ENTER,
      SPACE,
      RIGHT,
      DOWN
    } = KEYBOARD_KEYS;
    const {
      key,
      shiftKey
    } = event; // To know if current activeElement is last element in MegaMenu.

    const lastElementActive = activeElement === focusableElements.current.length - 1;
    /**
     * On `space` key we need to,
     * - close MegaMenu.
     * - redirect to path provided in `href`.
     */

    if (key === SPACE) {
      setShowMegaMenu(false);
      event.target.click();
    }
    /**
     * On `enter` key we need to,
     * - close MegaMenu.
     * - redirect to path provided in `href`.
     */


    if (key === ENTER) {
      setShowMegaMenu(false);
      /**
       * We are stopping propagation to prevent MegaMenu from reopening
       * from NavBarItem(parent) click.
       */

      event.preventDefault();
      event.stopPropagation();
      event.target.click();
    }

    if (key === TAB) {
      /**
       * We want to do `preventDefault` in all cases expect for last
       * MegaMenuItem in last `NavBarItem` so that focus moves out of NavBar.
       */
      if (lastNavBarItem) {
        !lastElementActive && event.preventDefault();
      } else {
        event.preventDefault();
      }
      /**
       * Increment activeElement by 1.
       * If activeElement is not last element in MegaMenu and `shift`
       * is not used.
       * Decrement activeElement by 1.
       * If activeElement is not first element in MegaMenu and `shift` key is
       * used with `tab`.
       */


      if (!lastElementActive && !shiftKey) {
        setActiveElement(activeElement + 1);
      } else if (activeElement !== 0 && shiftKey) {
        setActiveElement(activeElement - 1);
      }
      /**
       * If first element is activeElement and `shift` key is used
       * close MegaMenu.
       */


      if (activeElement === 0 && shiftKey) {
        setShowMegaMenu(false);
      }
      /**
       * If last element is active and `shift` key is not used close MegaMenu.
       */


      if (lastElementActive && !shiftKey) {
        setShowMegaMenu(false);
        /**
         * If NavBarItem is not last activeElement in NavBar focus next
         * NavBarItem when MegaMenu closes.
         */

        !lastNavBarItem && handleKeyboardActions();
      }
    }
    /**
     * If activeElement is first element and `left` or `up` key is used
     * close MegaMenu else move focus to previous focusable MegaMenuItem.
     */


    if (key === LEFT || key === UP) {
      if (activeElement === 0) {
        setShowMegaMenu(false);
      } else {
        setActiveElement(activeElement - 1);
      }
    }
    /**
     * If activeElement is not last activeElement in MegaMenu and `right` or
     * `down` arrow key is used move focus to next focusable MegaMenuItem.
     */


    if (key === RIGHT || key === DOWN) {
      if (!lastElementActive) {
        setActiveElement(activeElement + 1);
      } else if (!lastNavBarItem) {
        setShowMegaMenu(false);
        /**
         * If NavBarItem is not last activeElement in NavBar focus next
         * NavBarItem when MegaMenu closes.
         */

        handleKeyboardActions();
      }
    }
  }; // This effect should execute only during initial load of MegaMenu.


  useEffect(() => {
    // Get all non disabled activeElements in MegaMenu on initial load.
    focusableElements.current = megaMenuRef.current.querySelectorAll("li a:not([disabled]), li button:not([disabled])"); // Should focus first MegaMenuItem when MegaMenu opens.

    focusableElements.current[0].focus();
  }, []);
  /**
   * This effect is used to focus current active element based on `tab` and
   * `arrow` key interactions.
   */

  useEffect(() => {
    focusableElements.current[activeElement].focus();
  }, [activeElement]);
  return _jsx(React.Fragment, {
    children: _jsxs("ul", _objectSpread(_objectSpread({
      ref: megaMenuRef,
      "aria-label": childrenAriaLabel,
      css: megaMenuList,
      onKeyDown: handleKeydown,
      className: OVERRIDE
    }, otherProps), {}, {
      children: [header && _jsx("li", {
        css: hopLinkWrapper,
        className: OVERRIDE,
        children: renderHubLink()
      }), children]
    }))
  });
}

MegaMenu.propTypes = process.env.NODE_ENV !== "production" ? {
  // Aria label for list of clickable links displayed in MegaMenu.
  childrenAriaLabel: PropTypes.string.isRequired,

  /**
   * Children will be displayed inside MegaMenu.
   * Children of MegaMenu can only be MegaMenuItemGroup component.
   */
  // eslint-disable-next-line react/require-default-props
  children: invalidIf(PropTypes.node.isRequired, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== MEGAMENUITEMGROUP) {
        error = true;
      }
    });
    return error;
  }, `'MegaMenu' children should be of type '${MEGAMENUITEMGROUP}'.`),
  // This prop displays hop link on top of MegaMenu.
  header: PropTypes.shape({
    // To disable a hub link and prevent any action on it.
    disabled: PropTypes.bool,

    /**
     * Label to displayed on hop link.
     * Required if `routeTo` is of type `string` or `object`.
     */
    label: requiredIf(PropTypes.string, props => !props.label && typeof props.routeTo !== "object", "when 'routeTo' is of type string or object"),

    /**
     * It can be a string to redirect or an object or a `NavLink` or `Link`
     * components of react router.
     */
    routeTo: invalidIf(PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.shape({
      url: requiredIf(PropTypes.string, props => !props.url && !props.onClick, "when `onClick` is not passed"),
      target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top"]),
      onClick: PropTypes.func
    })]).isRequired, (props, propName) => {
      let error = false;
      const prop = props[propName];
      const propType = typeof prop;

      if (propType === "object" && prop) {
        var _prop$type;

        const displayName = (_prop$type = prop.type) === null || _prop$type === void 0 ? void 0 : _prop$type.displayName;

        if ((displayName || prop.type) && displayName !== LINK && displayName !== NAVLINK) {
          error = true;
        }
      }

      return error;
    }, `'routeTo' should be of type 'string' or 'object' or accepts only 'Link' or 'NavLink' as components.`)
  })
} : {};
MegaMenu.defaultProps = {
  header: null
};
MegaMenu.displayName = MEGAMENU;
export default MegaMenu;